import React, { useEffect, useMemo, useState, useRef, useCallback } from "react"
import * as url from "../../helpers/url_helper"
import { API_ENCRYPTION_MODES } from "helpers/api_constants"

import {
    Alert,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormFeedback,
    FormGroup,
    Label,
    Row,
} from "reactstrap"

import { useFormik } from "formik"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import { validateBeforeConfirm } from "helpers/validation_helper"
import Dropzone from "react-dropzone"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import {
    addNewMerchant,
    getMerchantDetail,
} from "store/merchants/actions"
import {
    clearMerchantDetail,
    clearMerchantCommunications,
    clearMerchantContacts,
} from "store/merchants/actions"
import { getCitiesAction as onGetCities, getMunicipalitiesAction as onGetMunicipalities, getBrands as onGetBrands, getBrandDetail as onGetBrandDetail } from "store/actions"
import { getMerchantGroup as onGetMerchantGroups, getMerchantGroupDetail as onGetMerchantGroupDetail } from "store/merchant-group/actions"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import { uploadBase64File, uploadMultiPartFile } from "../../helpers/backend_helper"
import TextInput from "components/Common/TextInput"
import TextArea from "components/Common/TextArea"
import SelectInput from "components/Common/SelectInput"
import CheckboxInput from "components/Common/CheckboxInput"
import RadioInput from "components/Common/RadioInput"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import EntitySelectionModal from "components/Common/EntitySelectionModal"
import GoogleMapLocationPicker from "components/Common/GoogleMapLocationPicker"
import LocationMapDisplay from "components/Common/LocationMapDisplay"
import PopupModal from "components/Common/PopupModel"
import MerchantGroupDetail from "../Merchant-Group/merchant-group-detail"

export default function MerchantForm({
    page,
    match,
    history,
    onMerchantCreated,
    onTabCompletion,
    navigateToNextTab,
    activeTab,
    onFormRef,
    onReadOnlyChange,
    onFormChange,
}) {
    const [selectedFiles, setselectedFiles] = useState([])
    const [logoCleared, setLogoCleared] = useState(false)

    const [selectedPdfFiles, setSelectedPdfFiles] = useState([])
    const [priceGuideCleared, setPriceGuideCleared] = useState(false)
    const [readOnly, setReadOnly] = useState(page === "detail")
    const [isUploading, setIsUploading] = useState(false)
    const [merchantID, setMerchantID] = useState(null)
    const previousMerchantsLength = useRef(0)
    const previousError = useRef(null)

    // Brand selection modal state
    const [showBrandModal, setShowBrandModal] = useState(false)
    const [selectedBrand, setSelectedBrand] = useState(null)
    const [brandCleared, setBrandCleared] = useState(false)

    // Merchant Group selection modal state
    const [showMerchantGroupModal, setShowMerchantGroupModal] = useState(false)
    const [selectedMerchantGroup, setSelectedMerchantGroup] = useState(null)
    const [merchantGroupCleared, setMerchantGroupCleared] = useState(false)

    // Location selection modal state
    const [showLocationModal, setShowLocationModal] = useState(false)

    // Merchant detail modal state
    const [showMerchantDetailModal, setShowMerchantDetailModal] = useState(false)

    const detailID = match?.params?.id

    const dispatch = useDispatch()
    const { data, appInfo, cities, municipalities, brands, brandDetail, merchantGroups, merchantGroupDetail } = useSelector(state => ({
        data: state.merchants,
        appInfo: state.appInfo.appInfo,
        cities: state.appInfo.cities || [],
        municipalities: state.appInfo.municipalities || [],
        brands: state.brands.brands || [],
        brandDetail: state.brands.brandDetail,
        merchantGroups: state.merchantGroup.merchantGroups || [],
        merchantGroupDetail: state.merchantGroup.merchantGroupDetail,
    }))

    const merchant =
        page === "add"
            ? null
            : data?.merchantDetail && Array.isArray(data.merchantDetail)
                ? data.merchantDetail.find(m => m.merchantID === detailID)
                : data?.merchantDetail

    //Merchant Detail Data
    useEffect(() => {
        if (page === "detail" && !merchant) {
            // Check for updated parameter in URL to bust cache
            const urlParams = new URLSearchParams(window.location.search)
            const isUpdated = urlParams.get('updated') === 'true'

            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }

            // Add timestamp as URL query parameter for cache busting if updated parameter is present
            if (isUpdated) {
                options.params = { updated: Date.now() }
            }

            dispatch(getMerchantDetail(options))
        } else if (page === "add") {
            // Clear any existing merchant data when adding new merchant
            dispatch(clearMerchantDetail())
            dispatch(clearMerchantCommunications())
            dispatch(clearMerchantContacts())
        }
    }, [dispatch, page, detailID, merchant])

    // Clear all merchant data when component unmounts or detailID changes
    useEffect(() => {
        return () => {
            dispatch(clearMerchantDetail())
            dispatch(clearMerchantCommunications())
            dispatch(clearMerchantContacts())
        }
    }, [dispatch, detailID])

    // City dropdown styles are now handled by SelectInput component

    useEffect(() => {
        if (!cities.length && appInfo?.appCountryID) {
            dispatch(
                onGetCities({
                    headers: {
                        "input-parameters": { countryIDs: [appInfo.appCountryID] },
                    },
                })
            )
        }
    }, [dispatch, cities.length, appInfo?.appCountryID])

    useEffect(() => {
        if (!municipalities.length) {
            dispatch(
                onGetMunicipalities({
                    headers: {
                        "input-parameters": {},
                    },
                })
            )
        }
    }, [dispatch, municipalities.length])

    // Handle logo initialization in readonly mode
    useEffect(() => {
        if (page === "detail" && readOnly === true && merchant?.logo && !logoCleared) {
            const currentPreview = selectedFiles[0]?.preview || ""
            const isLocalBlobUrl = currentPreview.startsWith("blob:")

            if (isLocalBlobUrl) {
                return
            }

            const file = {
                preview: merchant.logo,
            }
            setselectedFiles([file])
        }
    }, [merchant, page, readOnly, logoCleared])

    // Handle price guide file initialization in readonly mode
    useEffect(() => {
        if (page === "detail" && readOnly === true && merchant?.priceGuide && !priceGuideCleared) {
            const currentPreview = selectedPdfFiles[0]?.preview || ""
            const isLocalBlobUrl = currentPreview.startsWith("blob:")

            if (isLocalBlobUrl) {
                return
            }

            const url = merchant.priceGuide
            const extension = url.split('.').pop()?.toLowerCase()

            // Determine file type from URL extension
            const isPdfFile = extension === 'pdf'
            const isImageFile = ['png', 'jpg', 'jpeg'].includes(extension)

            const fileName = isPdfFile ? "Price Guide.pdf" :
                isImageFile ? `Price Guide.${extension}` :
                    "Price Guide"

            const priceGuideFile = {
                preview: url,
                name: fileName,
                isPdf: isPdfFile,
                isImage: isImageFile
            }
            setSelectedPdfFiles([priceGuideFile])
        }
    }, [merchant, page, readOnly, priceGuideCleared])

    // Load selected brand in edit mode
    useEffect(() => {
        if (page === "detail" && !selectedBrand && !brandCleared) {
            // Check if we have a brand object directly in merchant data
            if (merchant?.brand && merchant.brand.brandID) {
                setSelectedBrand(merchant.brand)
            }
            // Fallback to loading brand details using brandID (backward compatibility)
            else if (merchant?.brandID) {
                dispatch(onGetBrandDetail({
                    headers: {
                        "input-parameters": { brandIDs: [merchant?.brand?.brandID || merchant.brandID] },
                    },
                }))
            }
        }
    }, [merchant?.brand, merchant?.brandID, page, selectedBrand, brandCleared, dispatch])

    // Set selected brand when brand detail is loaded (for backward compatibility)
    useEffect(() => {
        if (brandDetail && !selectedBrand && page === "detail" && !brandCleared && !merchant?.brand) {
            // brandDetail can be an array or single object based on the API response
            const brand = Array.isArray(brandDetail)
                ? brandDetail.find(b => b.brandID === (merchant?.brand?.brandID || merchant?.brandID))
                : brandDetail

            if (brand) {
                setSelectedBrand(brand)
            }
        }
    }, [brandDetail, selectedBrand, page, merchant?.brandID, merchant?.brand, brandCleared])

    // Load selected merchant group in edit mode
    useEffect(() => {
        if (page === "detail" && merchant?.merchantGroupID && !selectedMerchantGroup && !merchantGroupCleared) {
            dispatch(onGetMerchantGroupDetail({
                headers: {
                    "input-parameters": { merchantGroupID: merchant.merchantGroupID },
                },
            }))
        }
    }, [merchant?.merchantGroupID, page, selectedMerchantGroup, merchantGroupCleared, dispatch])

    // Set selected merchant group when merchant group detail is loaded
    useEffect(() => {
        if (merchantGroupDetail && !selectedMerchantGroup && page === "detail" && !merchantGroupCleared) {
            // merchantGroupDetail can be an array or single object based on the API response
            const merchantGroup = Array.isArray(merchantGroupDetail)
                ? merchantGroupDetail.find(mg => mg.merchantGroupID === merchant?.merchantGroupID)
                : merchantGroupDetail

            if (merchantGroup) {
                setSelectedMerchantGroup(merchantGroup)
            }
        }
    }, [merchantGroupDetail, selectedMerchantGroup, page, merchant?.merchantGroupID, merchantGroupCleared])

    const citiesOptions = useMemo(() => {
        return cities.map(city => ({
            value: city.cityID,
            label: city.name,
        }))
    }, [cities])

    const municipalitiesOptions = useMemo(() => {
        return municipalities.map(municipality => ({
            value: municipality.municipalityID,
            label: municipality.municipalityName,
        }))
    }, [municipalities])

    const barcodeReaderOptions = useMemo(() => [
        {
            value: "qr_code",
            label: "QR Code",
            icon: "bx bx-qr-scan"
        },
        {
            value: "pdf_417",
            label: "PDF 417",
            icon: "bx-scan-barcode"
        },
        {
            value: "code_128",
            label: "Code 128",
            icon: "bx bx-barcode"
        }
    ], [])



    // Brand columns for modal - matching brands-list.js exactly
    const brandColumns = useMemo(() => [
        {
            Header: "Logo",
            accessor: "logo",
            disableFilters: true,
            filterable: true,
            accessor: cellProps => (
                <>
                    {!cellProps.logo ? (
                        <div className="avatar-xs">
                            <span className="avatar-title rounded-circle">
                                {cellProps.name.charAt(0)}
                            </span>
                        </div>
                    ) : (
                        <div>
                            <img
                                className="rounded-circle avatar-xs"
                                src={cellProps.logo}
                                alt=""
                            />
                        </div>
                    )}
                </>
            ),
        },
        {
            Header: "Name",
            accessor: "name",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
        {
            Header: "Description",
            accessor: "description",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? (
                    <div>
                        {cellProps.value.length > 100
                            ? cellProps.value.substring(0, 100) + "..."
                            : cellProps.value}
                    </div>
                ) : (
                    ""
                )
            },
        }
    ], [])

    // Merchant Group columns for modal - matching merchants-group-list.js structure
    const merchantGroupColumns = useMemo(() => [
        {
            Header: "Logo",
            accessor: "logo",
            disableFilters: true,
            filterable: true,
            accessor: cellProps => (
                <>
                    {!cellProps.logo ? (
                        <div className="avatar-xs">
                            <span className="avatar-title rounded-circle">
                                {cellProps.legalEntityName.charAt(0)}
                            </span>
                        </div>
                    ) : (
                        <div>
                            <img
                                className="rounded-circle avatar-xs"
                                src={cellProps.logo}
                                alt=""
                            />
                        </div>
                    )}
                </>
            ),
        },
        {
            Header: "Display Name",
            accessor: "displayName",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? (
                    <div>
                        {cellProps.value.length > 50
                            ? cellProps.value.substring(0, 50) + "..."
                            : cellProps.value}
                    </div>
                ) : (
                    "---"
                )
            },
        },
        {
            Header: "Legal Entity Name",
            accessor: "legalEntityName",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
        {
            Header: "Legal Entity Address",
            accessor: "legalEntityAddress",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? (
                    <div>
                        {cellProps.value.length > 50
                            ? cellProps.value.substring(0, 50) + "..."
                            : cellProps.value}
                    </div>
                ) : (
                    "---"
                )
            },
        },
    ], [])

    async function handleAcceptedFiles(files) {
        if (files.length > 0) {
            const file = files[0]

            const base64Img = await convertFileToBase64(file)

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    forExistingResource: false,
                    base64encodedFileByteArray: base64Img,
                    resourceType: "EUROPA_MERCHANT_LOGO",
                })
                setselectedFiles([file])
                setLogoCleared(false) // Reset cleared flag when uploading new image
                validation.setFieldValue("logo", file)
            } catch (error) {
                console.error("Error converting to base64:", error)
            }
        }
    }
    
    async function handleAcceptedPdfFiles(files) {
        if (files.length > 0) {
            const file = files[0]

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    forExistingResource: false,
                    resourceType: "EUROPA_MERCHANT_PRICE_GUIDE",
                    isPdf: false
                })
                setSelectedPdfFiles([file])
                setPriceGuideCleared(false) // Reset cleared flag when uploading new file
                validation.setFieldValue("priceGuideFile", file)
            } catch (error) {
                console.error("Error processing PDF file:", error)
            }
        }
    }

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            legalEntityName: merchant?.legalEntityName || "",
            displayName: merchant?.displayName || "",
            legalEntityAddress: merchant?.legalEntityAddress || "",
            city: merchant?.city
                ? {
                    value: merchant.city.cityID,
                    label: merchant.city.name,
                }
                : null,
            municipality: merchant?.municipality
                ? {
                    value: merchant.municipality.municipalityID,
                    label: merchant.municipality.municipalityName,
                }
                : null,
            municipalityTaxApplicable: merchant?.municipalityTaxApplicable === "true" || merchant?.municipalityTaxApplicable === true,
            briefInfo: merchant?.briefInfo || "",
            description: merchant?.description || "",
            wwliTitle: merchant?.wwliTitle || "",
            wwliBody: merchant?.wwliBody || "",
            refundPolicyDays: merchant?.refundPolicyDays || "",
            priceGuide: merchant?.priceGuide || "",
            taxRegnNum: merchant?.taxRegnNum || "",
            barcodeReader: merchant?.barcodeReader || "",
            brandID: merchant?.brand?.brandID || merchant?.brandID || "",
            merchantGroupID: merchant?.merchantGroupID || "",
            geoLoc: {
                posLat: merchant?.geoLoc?.posLat || merchant?.latitude || "",
                posLong: merchant?.geoLoc?.posLong || merchant?.longitude || "",
            },
            merchantID: merchant?.merchantID || "",
            logo: merchant?.logo
                ? {
                    preview: merchant.logo,
                }
                : "",
            priceGuideFile: merchant?.priceGuide
                ? {
                    preview: merchant.priceGuide,
                    isPdf: true
                }
                : "",
        },
        validationSchema: Yup.object({

            brandID: Yup.string().required("Please Select Brand"),

            merchantGroupID: Yup.string().required("Please Select Merchant Group"),

            legalEntityName: Yup.string()
                .required("Please Enter Legal Entity Name")
                .max(255, "Legal Entity Name cannot be greater than 255"),

            displayName: Yup.string()
                .required("Please Enter Display Name")
                .max(255, "Display Name cannot be greater than 255"),

            legalEntityAddress: Yup.string()
                .required("Please Enter Legal Entity Address")
                .max(255, "Legal Entity Address cannot be greater than 255"),

            description: Yup.string().max(
                255,
                "Description cannot be greater than 255"
            ),

            briefInfo: Yup.string().max(255, "Brief Info cannot be greater than 255"),

            wwliTitle: Yup.string().max(255, "WWLI Title cannot be greater than 255"),

            wwliBody: Yup.string().max(255, "WWLI Body cannot be greater than 255"),


            refundPolicyDays: Yup.number()
                .typeError("Refund Policy Days must be a number")
                .required("Please Enter Refund Policy Days")
                .integer("Refund Policy Days must be a whole number")
                .min(0, "Refund Policy Days must be between 0 and 30")
                .max(30, "Refund Policy Days must be between 0 and 30"),

            priceGuide: Yup.string().max(255, "Price Guide cannot be greater than 255"),

            taxRegnNum: Yup.string().required("Please Enter Tax Registration Number").max(255, "Tax Registration Number cannot be greater than 255"),

            barcodeReader: Yup.string(),

            geoLoc: Yup.object().shape({
                posLat: Yup.number().required("Please Select Location on Map"),
                posLong: Yup.number().required("Please Select Location on Map"),
            }),

            city: Yup.object().nullable().required("Please Select City"),

            municipality: Yup.object().nullable().required("Please Select Municipality"),

            municipalityTaxApplicable: Yup.boolean(),

            logo: Yup.mixed()
                .nullable()
                .when([], {
                    is: () => page === "add",
                    then: schema => schema.optional("Please Upload Merchant Logo"),
                    otherwise: schema => schema,
                })
                .test(
                    "merchantImage",
                    "Invalid Merchant image",
                    async function (value) {
                        if (!value && page !== "add") {
                            return true
                        }

                        if (!value) {
                            return true
                        }

                        // If it's an existing logo (has preview but no file properties), skip validation
                        if (value.preview && !value.type && !value.size) {
                            return true
                        }

                        try {
                            await validateImageFile(value, "merchant", 550, 550, 1) // 550x550, 1MB
                            return true
                        } catch (error) {
                            return this.createError({ message: error.message })
                        }
                    }
                ),

            priceGuideFile: Yup.mixed()
                .nullable()
                .test(
                    "priceGuideFile",
                    "Invalid file",
                    async function (value) {
                        if (!value) {
                            return true
                        }

                        if (value.isPdf && value.preview) {
                            return true
                        }

                        if (value.type) {
                            const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
                            if (!allowedTypes.includes(value.type)) {
                                return this.createError({ message: "Only PDF, PNG, and JPEG files are allowed" })
                            }
                        }

                        if (value.size && value.size > 5 * 1024 * 1024) { // 5MB limit
                            return this.createError({ message: "File size must be less than 5MB" })
                        }

                        return true
                    }
                ),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })



    // Ref to hold current validation object to prevent rerenders
    const validationRef = useRef(validation)

    // Update validation ref whenever validation changes
    useEffect(() => {
        validationRef.current = validation
    }, [validation])

    // Custom submit function that includes validation
    const handleSubmitWithValidation = async () => {
        const isValid = await validateBeforeConfirm(validationRef.current)
        if (isValid) {
            handleConfirmedSubmit()
        }
    }

    // Expose form methods to parent component
    useEffect(() => {
        if (onFormRef && page === "add") {
            onFormRef({
                handleSubmit: async () => {
                    const isValid = await validateBeforeConfirm(validationRef.current)
                    if (isValid) {
                        handleConfirmedSubmit()
                    }
                },
                submitForm: () =>
                    new Promise(async resolve => {
                        const isValid = await validateBeforeConfirm(validationRef.current)
                        if (isValid) {
                            handleConfirmedSubmit()
                        }
                        resolve()
                    }),
            })
        }
    }, [onFormRef, page]) // Removed validation from dependencies

    // Notify parent of form changes for breadcrumb secondary info
    useEffect(() => {
        if (onFormChange) {
            onFormChange({
                legalEntityName: validation.values.legalEntityName || "",
                legalEntityAddress: validation.values.legalEntityAddress || ""
            })
        }
    }, [validation.values.legalEntityName, validation.values.legalEntityAddress])

    // Function to handle the actual form submission after confirmation
    const handleConfirmedSubmit = async () => {
        setIsUploading(true)
        try {
            const values = validationRef.current.values
            const logoValue = values.logo
            const pdfValue = values.priceGuideFile
            let logoData = ""
            let priceGuideFileName = ""

            // Handle logo upload logic
            if (logoValue && logoValue.base64encodedFileByteArray) {
                // User uploaded a new image
                try {
                    // Call the API directly instead of through dispatch
                    const uploadResponse = await uploadBase64File(logoValue, {})

                    if (uploadResponse && uploadResponse.fileName) {
                        logoData = uploadResponse.fileName
                    } else {
                        throw new Error("Failed to upload logo - no filename returned")
                    }
                } catch (uploadError) {
                    console.error("Logo upload failed:", uploadError)
                    // You might want to show an error message to the user here
                    setIsUploading(false)
                    return
                }
            } else if (logoValue && logoValue.preview && !logoValue.base64encodedFileByteArray) {
                // User has existing image and didn't change it - extract filename from URL
                const url = logoValue.preview
                const urlParts = url.split('/')
                logoData = urlParts[urlParts.length - 1] // Get the last part of the URL (filename)
            } else if (logoValue === "") {
                // User explicitly cleared the logo
                logoData = ""
            }
            // Handle price guide upload logic
            if (pdfValue && !pdfValue.isPdf && pdfValue.resourceType) {
                // User uploaded a new price guide file
                try {
                    // Create FormData for multipart upload
                    const formData = new FormData()
                    formData.append('resource', pdfValue)

                    // Set query parameters for multipart upload
                    const params = {
                        forExistingResource: pdfValue.forExistingResource || false,
                        resourceType: pdfValue.resourceType
                    }

                    // Call the multipart API
                    const uploadResponse = await uploadMultiPartFile(formData, params)

                    if (uploadResponse && uploadResponse.fileName) {
                        priceGuideFileName = uploadResponse.fileName
                    } else {
                        throw new Error("Failed to upload PDF - no filename returned")
                    }
                } catch (uploadError) {
                    console.error("PDF upload failed:", uploadError)
                    // You might want to show an error message to the user here
                    setIsUploading(false)
                    return
                }
            } else if (pdfValue && pdfValue.preview && (pdfValue.isPdf || pdfValue.isImage)) {
                // User has existing price guide file and didn't change it - extract filename from URL
                const url = pdfValue.preview
                const urlParts = url.split('/')
                priceGuideFileName = urlParts[urlParts.length - 1] // Get the last part of the URL (filename)
            } else if (pdfValue === "") {
                // User explicitly cleared the price guide
                priceGuideFileName = ""
            }

            const payload = {
                legalEntityName: values.legalEntityName,
                displayName: values.displayName,
                legalEntityAddress: values.legalEntityAddress,
                cityID: values.city?.value || "",
                municipalityID: values.municipality?.value || "",
                municipalityTaxApplicable: values.municipalityTaxApplicable ? "true" : "false",
                briefInfo: values.briefInfo || "",
                description: values.description || "",
                wwliTitle: values.wwliTitle || "",
                wwliBody: values.wwliBody || "",
                refundPolicyDays: values.refundPolicyDays || "",
                priceGuide: priceGuideFileName || "",
                taxRegnNum: values.taxRegnNum || "",
                barcodeReader: values.barcodeReader || "",
                brandID: values.brandID || "",
                merchantGroupID: values.merchantGroupID || "",
                geoLoc: values.geoLoc || { posLat: "", posLong: "" },
                actionType: page === "add" && !merchantID ? "create" : "update",
                logo: logoData || "",
            }

            if (page === "detail" || merchantID) {
                payload.merchantID = values.merchantID || merchantID
            }

            const options = {
                // encDecAlgo: API_ENCRYPTION_MODES.BASE64,
            }

            // Now dispatch the create/update action with the uploaded logo filename
            dispatch(
                addNewMerchant(payload, options, page === "add" ? null : history)
            )

        } catch (error) {
            console.error("Form submission error:", error)
            setIsUploading(false)
        } finally {
            // For detail mode, always reset isUploading
            if (page === "detail") {
                setIsUploading(false)
            }
            // For add mode, reset isUploading after a delay to ensure error handling completes
            else if (page === "add") {
                setTimeout(() => {
                    setIsUploading(false)
                }, 2000) // Reset after 2 seconds as fallback
            }
        }
    }

    const clearLogo = () => {
        setselectedFiles([])
        setLogoCleared(true)
        validation.setFieldValue("logo", "")
    }

    const clearPdf = () => {
        setSelectedPdfFiles([])
        setPriceGuideCleared(true)
        validation.setFieldValue("priceGuideFile", "")
    }

    // Brand selection handlers
    const handleBrandModalOpen = useCallback(() => {
        setShowBrandModal(true)
    }, [])

    const handleBrandSelect = useCallback((brand) => {
        setSelectedBrand(brand)
        validation.setFieldValue("brandID", brand.brandID)
        setBrandCleared(false)
    }, [validation])

    const handleBrandClear = useCallback(() => {
        setSelectedBrand(null)
        validation.setFieldValue("brandID", "")
        setBrandCleared(true)
    }, [validation])

    // Brand fetch function for modal
    const handleBrandFetch = useCallback(({ search, page }) => {
        const startIndex = (page - 1) * 10 // Assuming page size is 10
        dispatch(
            onGetBrands({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }, [dispatch])

    // Merchant Group selection handlers
    const handleMerchantGroupModalOpen = useCallback(() => {
        setShowMerchantGroupModal(true)
    }, [])

    const handleMerchantGroupSelect = useCallback((merchantGroup) => {
        setSelectedMerchantGroup(merchantGroup)
        validation.setFieldValue("merchantGroupID", merchantGroup.merchantGroupID)
        setMerchantGroupCleared(false)
    }, [validation])

    const handleMerchantGroupClear = useCallback(() => {
        setSelectedMerchantGroup(null)
        validation.setFieldValue("merchantGroupID", "")
        setMerchantGroupCleared(true)
    }, [validation])

    // Merchant Group fetch function for modal
    const handleMerchantGroupFetch = useCallback(({ search, page }) => {
        const startIndex = (page - 1) * 10 // Assuming page size is 10
        dispatch(
            onGetMerchantGroups({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }, [dispatch])

    // Location selection handlers
    const handleLocationModalOpen = useCallback(() => {
        setShowLocationModal(true)
    }, [])

    const handleLocationSelect = useCallback((location) => {
        validation.setFieldValue("geoLoc", location)
    }, [validation])

    // Memoize current location to prevent re-renders
    const currentLocation = useMemo(() => {
        const geoLoc = validation.values.geoLoc
        if (geoLoc && geoLoc.posLat && geoLoc.posLong) {
            return {
                posLat: geoLoc.posLat,
                posLong: geoLoc.posLong
            }
        }
        return null
    }, [validation.values.geoLoc?.posLat, validation.values.geoLoc?.posLong])

    const handleLocationClear = useCallback(() => {
        validation.setFieldValue("geoLoc", { posLat: "", posLong: "" })
    }, [validation])

    // City selection is now handled by SelectInput component

    const { merchantError, merchants, creationSuccess } = useSelector(
        state => ({
            merchantError:
                state.merchants.error?.message ||
                state.merchants.error?.description,
            merchants: state.merchants.merchants,
            creationSuccess: state.merchants.creationSuccess,
        })
    )

    useEffect(() => {
        if (
            creationSuccess &&
            creationSuccess !== previousMerchantsLength.current
        ) {
            setReadOnly(true);
            setMerchantID(creationSuccess.merchantID || Date.now().toString());
            if (onReadOnlyChange) onReadOnlyChange(true);
            if (onMerchantCreated) onMerchantCreated(creationSuccess.merchantID);
            if (page === "detail") {
                const currentUrl = new URL(window.location);
                currentUrl.searchParams.set('updated', 'true');
                history.replace(currentUrl.pathname + currentUrl.search);

                // Clear image states if they were removed in the form
                const currentValues = validationRef.current.values;
                if (currentValues.logo === "") {
                    setselectedFiles([]);
                    setLogoCleared(true);
                }
                if (currentValues.priceGuideFile === "") {
                    setSelectedPdfFiles([]);
                    setPriceGuideCleared(true);
                }
            }
            setIsUploading(false);
            previousMerchantsLength.current = creationSuccess;
        }
    }, [
        creationSuccess,
        page,
        onMerchantCreated,
        onReadOnlyChange,
        navigateToNextTab,
        history
    ]);

    // Separate effect for error/spinner
    useEffect(() => {
        if (
            merchantError &&
            merchantError !== previousError.current
        ) {
            setIsUploading(false);
            previousError.current = merchantError;
        }
    }, [merchantError]);

    return (
        <>
            {((page === "detail" && readOnly) || (page === "add" && readOnly)) && (
                <div className="mb-3 d-flex justify-content-end">
                    <AccessControlWrapper
                        contextUri={url.MANAGE_MERCHANT}
                        actionType="post"
                    >
                        <CButton.Primary
                            text="Edit Merchant"
                            onClick={() => {
                                setReadOnly(false)
                                setBrandCleared(false) // Reset brand cleared state when entering edit mode
                                setMerchantGroupCleared(false) // Reset merchant group cleared state when entering edit mode
                                setLogoCleared(false) // Reset logo cleared flag when entering edit mode
                                setPriceGuideCleared(false) // Reset price guide cleared flag when entering edit mode
                                if (onReadOnlyChange) {
                                    onReadOnlyChange(false)
                                }
                            }}
                        />
                    </AccessControlWrapper>
                </div>
            )}

            {merchantError && (
                <Alert color="danger" className="mb-3">
                    {merchantError}
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Form
                        className="needs-validation"
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <Row>


                            {/* Merchant Group Selection */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>
                                        Merchant Group
                                        {!readOnly ? "*" : ""}
                                    </Label>
                                    {selectedMerchantGroup ? (
                                        <div className={`border rounded p-3 position-relative ${readOnly ? "input-as-text" : ""}`}>
                                            <div className="d-flex align-items-start">
                                                <div className="avatar-xs me-3">
                                                    {selectedMerchantGroup.logo ? (
                                                        <img
                                                            src={selectedMerchantGroup.logo}
                                                            alt=""
                                                            className="avatar-xs rounded-circle"
                                                        />
                                                    ) : (
                                                        <span className="avatar-title rounded-circle bg-primary">
                                                            {selectedMerchantGroup.legalEntityName?.charAt(0)?.toUpperCase() || "MG"}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold mb-1 flex align-items-center">{selectedMerchantGroup.displayName}
                                                        <AccessControlWrapper
                                                            contextUri={url.GET_MERCHANT_GROUP}
                                                            actionType="get"
                                                        >
                                                            <IconButton.View
                                                                className="ms-2"
                                                                onClick={() => setShowMerchantDetailModal(true)}
                                                                title="View Merchant Group Details"
                                                            />
                                                        </AccessControlWrapper>
                                                    </div>
                                                    <div className="text-muted mb-1">
                                                        {selectedMerchantGroup.legalEntityName}
                                                    </div>
                                                    <div className="text-muted">
                                                        {selectedMerchantGroup.legalEntityAddress && selectedMerchantGroup.legalEntityAddress.length > 50
                                                            ? selectedMerchantGroup.legalEntityAddress.substring(0, 50) + "..."
                                                            : selectedMerchantGroup.legalEntityAddress || "---"}
                                                    </div>
                                                </div>
                                                {!readOnly && (
                                                    <>

                                                        <IconButton
                                                            icon="bx bxs-x-circle"
                                                            variant="rounded-circle"
                                                            className="position-absolute right-0 top-0 start-100 translate-middle"
                                                            style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                            onClick={handleMerchantGroupClear}
                                                            title="Clear Merchant Group"
                                                        />
                                                    </>
                                                )}

                                            </div>
                                        </div>
                                    ) : readOnly ? (
                                        <div className="form-control input-as-text text-danger">
                                            Merchant Group not found
                                        </div>
                                    ) : (
                                        <div>
                                            <AccessControlWrapper
                                                contextUri={url.GET_MERCHANT_GROUP}
                                                actionType="get"
                                            >
                                                <CButton.Primary
                                                    text="Select Merchant Group"
                                                    icon="bx bx-plus"
                                                    onClick={handleMerchantGroupModalOpen}
                                                />
                                            </AccessControlWrapper>
                                        </div>
                                    )}
                                    {validation.touched.merchantGroupID && validation.errors.merchantGroupID ? (
                                        <FormFeedback type="invalid" className="d-block">
                                            {validation.errors.merchantGroupID}
                                        </FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            {/* Brand Selection */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>
                                        Brand
                                        {!readOnly ? "*" : ""}
                                    </Label>
                                    {selectedBrand ? (
                                        <div className={`d-flex border rounded p-2 position-relative ${readOnly ? "input-as-text" : ""}`}>
                                            <div className="avatar-xs me-2">
                                                {selectedBrand.logo ? (
                                                    <img
                                                        src={selectedBrand.logo}
                                                        alt=""
                                                        className="avatar-xs rounded-circle"
                                                    />
                                                ) : (
                                                    <span className="avatar-title rounded-circle bg-primary">
                                                        {selectedBrand.name?.charAt(0)?.toUpperCase() || "B"}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-bold mb-1">{selectedBrand.name}</div>
                                                <div className="text-muted  d-block">
                                                    {selectedBrand.description}
                                                </div>
                                            </div>
                                            {!readOnly && (
                                                <IconButton
                                                    icon="bx bxs-x-circle"
                                                    variant="rounded-circle"
                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                    onClick={handleBrandClear}
                                                    title="Clear Brand"

                                                />

                                            )}
                                        </div>
                                    ) : readOnly ? (
                                        <div className="form-control input-as-text text-danger">
                                            Brand not found
                                        </div>
                                    ) : (
                                        <div>
                                            <AccessControlWrapper
                                                contextUri={url.GET_BRANDS}
                                                actionType="get"
                                            >
                                                <CButton.Primary
                                                    text="Select Brand"
                                                    icon="bx bx-plus"
                                                    onClick={handleBrandModalOpen}
                                                />
                                            </AccessControlWrapper>
                                        </div>
                                    )}
                                    {validation.touched.brandID && validation.errors.brandID ? (
                                        <FormFeedback type="invalid" className="d-block">
                                            {validation.errors.brandID}
                                        </FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            {/* Legal Entity Name */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"legalEntityName"}
                                    placeholder={"Enter Legal Entity Name"}
                                    label={"Legal Entity Name"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Display Name */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"displayName"}
                                    placeholder="Enter Display Name"
                                    label={"Display Name"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Legal Entity Address */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"legalEntityAddress"}
                                    placeholder="Enter Legal Entity Address"
                                    label={"Legal Entity Address"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* City ID */}
                            <Col md="6">
                                <SelectInput
                                    name="city"
                                    label="City"
                                    placeholder="Select City..."
                                    options={citiesOptions}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Municipality ID */}
                            <Col md="6">
                                <SelectInput
                                    name="municipality"
                                    label="Municipality"
                                    placeholder="Select Municipality..."
                                    options={municipalitiesOptions}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Municipality Tax Applicable */}
                            <Col md="6">
                                <CheckboxInput
                                    name="municipalityTaxApplicable"
                                    label="Municipality Tax Applicable"
                                    checkboxLabel="Apply municipality tax"
                                    validation={validation}
                                    readOnly={readOnly}
                                    trueText="Yes"
                                    falseText="No"
                                />
                            </Col>


                        </Row>


                        {/* text area's row */}
                        <Row>
                            {/* Brief Info */}
                            <Col md="6">
                                <TextArea
                                    label={"Brief Info"}
                                    placeholder="Enter Brief Info"
                                    helperText={"Max 255 chars allowed"}
                                    name={"briefInfo"}
                                    validation={validation}
                                    required={false}
                                    readOnly={readOnly}
                                    maxLength={255}
                                />
                            </Col>

                            <Col md="6">
                                <TextArea
                                    label={"Description"}
                                    name={"description"}
                                    placeholder="Enter Description"
                                    type="textarea"
                                    readOnly={readOnly}
                                    validation={validation}
                                    required={false}
                                    maxLength={"255"}
                                    helperText={"Max 255 chars allowed"}
                                />
                            </Col>
                        </Row>

                        {/* Additional fields row */}
                        <Row>
                            {/* WWLI Title */}
                            <Col md="6">
                                <TextInput
                                    label={"WWLI Title"}
                                    name={"wwliTitle"}
                                    placeholder="Enter WWLI Title"
                                    type="text"
                                    readOnly={readOnly}
                                    validation={validation}
                                    required={false}
                                    maxLength={255}
                                    helperText={"Max 255 chars allowed"}
                                />
                            </Col>

                            {/* WWLI Body */}
                            <Col md="6">
                                <TextArea
                                    label={"WWLI Body"}
                                    name={"wwliBody"}
                                    placeholder="Enter WWLI Body"
                                    type="textarea"
                                    readOnly={readOnly}
                                    validation={validation}
                                    required={false}
                                    maxLength={255}
                                    helperText={"Max 255 chars allowed"}
                                />
                            </Col>
                        </Row>

                        {/* Additional fields row */}
                        <Row>

                            {/* Tax Registration Number */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"taxRegnNum"}
                                    placeholder="Enter Tax Registration Number"
                                    label={"Tax Registration Number"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required
                                />
                            </Col>

                            {/* Refund Policy Days */}
                            <Col md="6">
                                <TextInput
                                    label={"Refund Policy Days"}
                                    name={"refundPolicyDays"}
                                    placeholder="Enter Refund Policy Days (0-30)"
                                    type="text"
                                    readOnly={readOnly}
                                    validation={validation}
                                    required={true}
                                    maxLength={2}
                                    helperText={"Value between 0-30"}
                                />
                            </Col>

                            {/* Barcode Reader */}
                            <Col md="6">
                                <RadioInput
                                    name="barcodeReader"
                                    label="Barcode Reader"
                                    options={barcodeReaderOptions}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={false}
                                    showIcons={true}
                                    inline={true}
                                />
                            </Col>

                            {/* Price Guide Upload */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>Price Guide</Label>

                                    {(selectedPdfFiles.length === 0 || validation.errors.priceGuideFile) && readOnly === false ? (
                                        <>
                                            <Dropzone
                                                multiple={false}
                                                accept={{
                                                    "application/pdf": [".pdf"],
                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                }}
                                                onDrop={acceptedFiles => {
                                                    handleAcceptedPdfFiles(acceptedFiles)
                                                }}
                                            >
                                                {({ getRootProps, getInputProps }) => (
                                                    <div className="dropzone">
                                                        <div
                                                            className="dz-message needsclick mt-2"
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <div className="mb-3">
                                                                <i className="display-4 text-muted bx bx-file-blank" />
                                                            </div>
                                                            <h4>
                                                                Drop PDF, PNG, or JPEG file here or click to upload.
                                                            </h4>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        </>
                                    ) : (
                                        readOnly === true && selectedPdfFiles.length === 0 && <p className="fs-5 text-danger">No Price Guide found</p>
                                    )}

                                    {selectedPdfFiles.length > 0 && !validation.errors.priceGuideFile ? (
                                        <div className="dropzone-previews" id="pdf-file-previews">
                                            {selectedPdfFiles.map((f, i) => {
                                                return (
                                                    <Card
                                                        className="mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                        key={i + "-pdf-file"}
                                                    >
                                                        <div className="p-2">
                                                            {readOnly ? null : (
                                                                <IconButton
                                                                    icon="bx bxs-x-circle"
                                                                    variant="rounded-circle"
                                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                                    onClick={clearPdf}
                                                                    title={"Remove Price Guide"}
                                                                />
                                                            )}

                                                            <Row className="align-items-center">
                                                                <Col className="col-auto">
                                                                    <div
                                                                        className="avatar-sm rounded bg-light d-flex align-items-center justify-content-center"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => window.open(f.preview, '_blank')}
                                                                    >
                                                                        {f.type?.includes('pdf') || f.isPdf ? (
                                                                            <i className="bx bxs-file-pdf text-danger" style={{ fontSize: '2rem' }} />
                                                                        ) : f.type?.includes('image') || f.isImage ? (
                                                                            <i className="bx bxs-file-image text-primary" style={{ fontSize: '2rem' }} />
                                                                        ) : (
                                                                            <i className="bx bx-file text-secondary" style={{ fontSize: '2rem' }} />
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                                <Col>
                                                                    <div
                                                                        className="fw-bold"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => window.open(f.preview, '_blank')}
                                                                    >
                                                                        {f.name || "Price Guide"}
                                                                    </div>
                                                                    {f.formattedSize && (
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    )}
                                                                    <small className="text-muted">Click to open file</small>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </Card>
                                                )
                                            })}
                                        </div>
                                    ) : null}

                                    {validation.errors.priceGuideFile ? (
                                        <small
                                            className={`invalid-feedback d-inline-block align-top ${validation.touched.priceGuideFile && validation.errors.priceGuideFile
                                                ? "w-50"
                                                : "w-100"
                                                }`}
                                        >
                                            {validation.errors.priceGuideFile}
                                        </small>
                                    ) : null}

                                    {((selectedPdfFiles.length === 0 && !readOnly) ||
                                        validation.errors.priceGuideFile) && (
                                            <span className="text-muted text-end d-inline-block w-100">
                                                PDF, PNG, and JPEG files up to 5MB are allowed
                                            </span>
                                        )}
                                </FormGroup>
                            </Col>


                        </Row>


                        <Row>
                            {/* Location Selection */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>
                                        Location
                                        {!readOnly ? "*" : ""}
                                    </Label>
                                    {validation.values.geoLoc?.posLat && validation.values.geoLoc?.posLong ? (
                                        <div className={`position-relative ${readOnly ? "input-as-text" : ""}`}>
                                            {/* Location Info Header */}
                                            <div className="border rounded p-2 mb-2 d-flex align-items-center">
                                                <div className="avatar-xs me-2">
                                                    <span className="avatar-title rounded-circle bg-success">
                                                        <i className="bx bx-map"></i>
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold">Location Set</div>
                                                    <small className="text-muted">
                                                        Lat: {parseFloat(validation.values.geoLoc.posLat).toFixed(6)},
                                                        Lng: {parseFloat(validation.values.geoLoc.posLong).toFixed(6)}
                                                    </small>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    {!readOnly && (
                                                        <>
                                                            <IconButton
                                                                icon="bx bx-edit"
                                                                color="primary"
                                                                size="sm"
                                                                onClick={handleLocationModalOpen}
                                                                title="Change Location"
                                                            />
                                                            <IconButton
                                                                icon="bx bx-x"
                                                                color="danger"
                                                                size="sm"
                                                                onClick={handleLocationClear}
                                                                title="Clear Location"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Map Display */}
                                            <LocationMapDisplay
                                                location={validation.values.geoLoc}
                                                height="200px"
                                                zoom={12}
                                                showControls={false}
                                                appInfo={appInfo}
                                                className="mb-2"
                                            />
                                        </div>
                                    ) : readOnly ? (
                                        <div className="form-control input-as-text text-danger">
                                            Location not set
                                        </div>
                                    ) : (
                                        <div>
                                            <CButton.Primary
                                                text="Select Location"
                                                icon="bx bx-map"
                                                onClick={handleLocationModalOpen}
                                            />
                                        </div>
                                    )}
                                    {validation.touched.geoLoc && validation.errors.geoLoc ? (
                                        <FormFeedback type="invalid" className="d-block">
                                            {typeof validation.errors.geoLoc === 'object'
                                                ? validation.errors.geoLoc.posLat || validation.errors.geoLoc.posLong || "Please select a location"
                                                : validation.errors.geoLoc}
                                        </FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            {/* Logo */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>Logo{page === "add" && !readOnly ? "" : ""}</Label>

                                    {(selectedFiles.length === 0 || validation.errors.logo) && readOnly === false ? (
                                        <>
                                            <Dropzone
                                                multiple={false}
                                                accept={{
                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                }}
                                                onDrop={acceptedFiles => {
                                                    handleAcceptedFiles(acceptedFiles)
                                                }}
                                            >
                                                {({ getRootProps, getInputProps }) => (
                                                    <div className="dropzone">
                                                        <div
                                                            className="dz-message needsclick mt-2"
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <div className="mb-3">
                                                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                            </div>
                                                            <h4>
                                                                Drop Merchant image here or click to
                                                                upload.
                                                            </h4>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        </>
                                    ) : (
                                        readOnly === true && selectedFiles.length === 0 && <p className="fs-5 text-danger">No Logo found</p>
                                    )}

                                    {selectedFiles.length > 0 && !validation.errors.logo ? (
                                        <div className="dropzone-previews mt-3" id="file-previews">
                                            {selectedFiles.map((f, i) => {
                                                return (
                                                    <Card
                                                        className="d-inline-block mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                        key={i + "-file"}
                                                    >
                                                        <div className="p-2">
                                                            {readOnly ? null : (
                                                                <IconButton
                                                                    icon="bx bxs-x-circle"
                                                                    variant="rounded-circle"
                                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                                    onClick={clearLogo}
                                                                    title={"Remove Logo"}
                                                                />
                                                            )}

                                                            <div>
                                                                <div className="text-center mb-2">
                                                                    <img
                                                                        data-dz-thumbnail=""
                                                                        height="200"
                                                                        className="rounded bg-light"
                                                                        alt={f.name || "Logo"}
                                                                        src={f?.preview}
                                                                    />
                                                                </div>
                                                                {f.name && f.formattedSize ? (
                                                                    <div className="text-center">
                                                                        <Link
                                                                            to="#"
                                                                            className="text-muted font-weight-bold"
                                                                        >
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )
                                            })}
                                        </div>
                                    ) : null}

                                    {validation.errors.logo ? (
                                        <small
                                            className={`invalid-feedback d-inline-block align-top ${validation.touched.logo && validation.errors.logo
                                                ? "w-50"
                                                : "w-100"
                                                }`}
                                        >
                                            {validation.errors.logo}
                                        </small>
                                    ) : null}

                                    {((selectedFiles.length === 0 && !readOnly) ||
                                        validation.errors.logo) && (
                                            <span className="text-muted text-end d-inline-block w-100">
                                                jpg & png are allowed upto 1mb max (550x550)
                                            </span>
                                        )}
                                </FormGroup>
                            </Col>
                        </Row>



                        {/* Submit Button - Only show for detail/edit mode or add mode after creation */}
                        {!readOnly && (page === "detail" || (page === "add" && merchantID)) && (
                            <Row>
                                <Col md="12" className="text-end">
                                    <CButton.Secondary
                                        text="Cancel"
                                        className="me-2"
                                        onClick={() => {
                                            setReadOnly(true)
                                            setBrandCleared(false) // Reset brand cleared state
                                            setMerchantGroupCleared(false) // Reset merchant group cleared state
                                            setLogoCleared(false) // Reset logo cleared flag
                                            setPriceGuideCleared(false) // Reset price guide cleared flag
                                            if (onReadOnlyChange) {
                                                onReadOnlyChange(true)
                                            }

                                            // In add mode with created merchant, keep current form values
                                            // In edit mode, reset to original merchant values
                                            if (page === "add" && merchantID) {
                                                // For add mode after creation, just set readonly without resetting form
                                                // The form already has the values that were used to create the merchant
                                                return
                                            }

                                            // Reset form to original values (for edit mode)
                                            validation.resetForm()
                                            // Reset brand selection to original value
                                            if (page === "detail" && (merchant?.brand || merchant?.brandID)) {
                                                setSelectedBrand(null) // This will trigger reload
                                            }
                                            // Reset merchant group selection to original value
                                            if (page === "detail" && merchant?.merchantGroupID) {
                                                setSelectedMerchantGroup(null) // This will trigger reload
                                            }
                                            // Reset logo file selection
                                            if (page === "detail" && merchant?.logo) {
                                                const file = {
                                                    preview: merchant?.logo || "",
                                                }
                                                setselectedFiles([file])
                                            } else {
                                                setselectedFiles([])
                                            }
                                            // Reset price guide file selection
                                            if (page === "detail" && merchant?.priceGuide) {
                                                const url = merchant?.priceGuide || ""
                                                const extension = url.split('.').pop()?.toLowerCase()

                                                // Determine file type from URL extension
                                                const isPdfFile = extension === 'pdf'
                                                const isImageFile = ['png', 'jpg', 'jpeg'].includes(extension)

                                                const fileName = isPdfFile ? "Price Guide.pdf" :
                                                    isImageFile ? `Price Guide.${extension}` :
                                                        "Price Guide"

                                                const priceGuideFile = {
                                                    preview: url,
                                                    name: fileName,
                                                    isPdf: isPdfFile,
                                                    isImage: isImageFile
                                                }
                                                setSelectedPdfFiles([priceGuideFile])
                                            } else {
                                                setSelectedPdfFiles([])
                                            }
                                        }}
                                        disabled={isUploading}
                                    />
                                    <ConfirmButton
                                        validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                        onConfirm={handleConfirmedSubmit}
                                        message="Are you sure you want to update this Merchant? This action will modify the existing Merchant information."
                                        color="primary"
                                        disabled={isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Uploading...
                                            </>
                                        ) : (
                                            "Update Merchant"
                                        )}
                                    </ConfirmButton>
                                </Col>
                            </Row>
                        )}

                        {/* Create Button - Only show for add mode when not readonly */}
                        {!readOnly && !merchantID && page === "add" && (
                            <Row>
                                <Col md="12" className="text-end">
                                    <ConfirmButton
                                        validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                        onConfirm={handleConfirmedSubmit}
                                        message="Are you sure you want to create this Merchant?"
                                        color="primary"
                                        disabled={isUploading}
                                        contextUri={url.MANAGE_MERCHANT}
                                        actionType="post"
                                    >
                                        {isUploading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Creating...
                                            </>
                                        ) : (

                                            "Create Merchant"
                                        )}
                                    </ConfirmButton>
                                </Col>
                            </Row>
                        )}
                    </Form>
                </CardBody>
            </Card>

            {/* Brand Selection Modal */}
            <EntitySelectionModal
                isOpen={showBrandModal}
                toggle={() => setShowBrandModal(false)}
                title="Select Brand"
                data={brands}
                columns={brandColumns}
                onItemSelect={handleBrandSelect}
                selectedItemId={selectedBrand?.brandID}
                idField="brandID"
                onFetchData={handleBrandFetch}
                searchPlaceholder="Search brands..."
                emptyMessage="No brands found"
                refreshContextUri={url.GET_BRANDS}
                history={history}
            />

            {/* Merchant Group Selection Modal */}
            <EntitySelectionModal
                isOpen={showMerchantGroupModal}
                toggle={() => setShowMerchantGroupModal(false)}
                title="Select Merchant Group"
                data={merchantGroups}
                columns={merchantGroupColumns}
                onItemSelect={handleMerchantGroupSelect}
                selectedItemId={selectedMerchantGroup?.merchantGroupID}
                idField="merchantGroupID"
                onFetchData={handleMerchantGroupFetch}
                searchPlaceholder="Search merchant groups..."
                emptyMessage="No merchant groups found"
                refreshContextUri={url.GET_MERCHANT_GROUP}
                history={history}
            />

            {/* Location Selection Modal */}
            <GoogleMapLocationPicker
                isOpen={showLocationModal}
                toggle={() => setShowLocationModal(false)}
                title="Select Merchant Location"
                onLocationSelect={handleLocationSelect}
                currentLocation={currentLocation}
                appInfo={appInfo}
            />

            {/* Merchant Group Detail Modal */}
            {selectedMerchantGroup && (

                <PopupModal
                    isOpen={showMerchantDetailModal}
                    toggle={() => setShowMerchantDetailModal(false)}
                    title={`Merchant Group Details - ${selectedMerchantGroup.displayName}`}
                    size="xl"
                    showFooter={false}
                    bodyClassName="p-0"
                >
                    <MerchantGroupDetail
                        match={{ params: { id: selectedMerchantGroup.merchantGroupID } }}
                        history={history}
                    />
                </PopupModal>

            )}
        </>
    )
}